import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import useTaskBoard from '../../hooks/useTaskBoard';
import useAppChrome from '../../hooks/useAppChrome';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
    Button, Drawer, ErrorState, LoadingState, PillList, ToastRegion,
} from '../ui';
import UserSearch from '../shared/UserSearch';
import {
    CLOSED_WINDOW_DAYS, columnEmptyText, filterPills, GROUP_OPTIONS, isOpenTask, kbdHintText, QUICK_VIEWS,
    quickViewSummaries, TYPE_FILTERS, typeChipCounts,
} from '../../utils/taskBoard';
import {
    fromLocation, saveTasksSnapshot, takeTasksSnapshot, unitPath,
} from '../../utils/unitRoute';
import scrollInstant, { cssEscape } from '../../utils/scrollInstant';
import DropCancelConfirm from '../shared/DropCancelConfirm';
import TaskCardV2, { Avatar } from './TaskCardV2';
import TaskPreview from './TaskPreview';
import {
    CaretIcon, DealerIcon, SearchIcon, StatusGlyph,
} from './icons';
import '../ui/ui.css';
import './TasksV2.css';

// Below this width the preview is a drawer over the page (as the inventory's).
export const PREVIEW_DRAWER_QUERY = '(max-width: 1400px)';
export const KBD_HINT_ID = 'task-board-kbd-hint';
// A single click opens the preview only once a double-click can no longer
// follow (QA T-4): a double-click opens the unit without a preview flash.
export const PREVIEW_CLICK_DELAY_MS = 250;

const formatToday = (date) => date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
const count = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * The redesigned Task Board (shown with the "new design" flag on). All state
 * and behaviour come from hooks/useTaskBoard and hooks/useTaskCard, shared
 * with today's board. New here: the dealership chip (N-11/12/13), tile
 * sub-lines (N-03), type counts (N-04), filter pills (T4), the visible key
 * hint (N-08), the card preview (T2, D10), the "vs AI" chip (T1), the drain
 * bar on the Undo toast (T4), an error state with Retry (G-14) and Back from
 * the unit restoring the view (D7). The dealership name, user menu and Sign
 * Out are the v2 top bar's.
 */
function TaskBoardV2({ orgName }) {
    const location = useLocation();
    const navigate = useNavigate();
    // Back from the unit page (D7): the view the user left, exactly as it was.
    const [restore] = useState(() => takeTasksSnapshot(location));
    const board = useTaskBoard({ orgName, restore });
    const {
        canEdit, readOnly, dealership, activities, users, loading, error, retry,
        inventoryUnits, unitsLoading, unitFor, unitStatusFor, canOpenStock, nameFor,
        view, search, setSearch, typeFilter, setTypeFilter, assignee, setAssignee,
        quickView, toggleQuickView, groupBy, chooseGroupBy, showClosed, chooseShowClosed,
        clearFilters, clearFilter, filtersActive, filtered, hiddenClosed, columns, quickCounts, now,
        boardRef, dragEnabled, dragItem, setDragItem, dropTarget, setDropTarget, handleDrop, endDrag,
        handleStatusChange, handleQuickStatus, handleEdit, handleDelete, handleNavigate,
        toast, undo, dismissToast, branches,
    } = board;
    const { canSwitch, switchDealership } = useAppChrome({ orgName });
    const drawerMode = useMediaQuery(PREVIEW_DRAWER_QUERY);
    const searchRef = useRef(null);
    const [today] = useState(() => new Date());

    // ── Preview (T2, D10) ────────────────────────────────────────────────
    const [previewId, setPreviewId] = useState(restore?.previewId || null);
    const previewTask = previewId ? filtered.find((t) => String(t.id) === previewId) || null : null;
    const previewUnit = previewTask ? unitFor(previewTask) : null;
    const showPreview = Boolean(previewTask && previewUnit);
    const openBtnRef = useRef(null);
    const [focusPreview, setFocusPreview] = useState(false);
    const [refocusId, setRefocusId] = useState(null);

    const focusCard = useCallback((id, opts) => {
        const card = boardRef.current?.querySelector(`[data-task-id="${cssEscape(String(id))}"]`);
        card?.focus(opts);
    }, [boardRef]);

    // A preview whose task has left the board (filtered, deleted) closes.
    useEffect(() => {
        if (previewId && !loading && !showPreview) setPreviewId(null);
    }, [previewId, loading, showPreview]);

    const closePreview = useCallback(() => {
        setRefocusId(previewId);
        setPreviewId(null);
    }, [previewId]);
    // Focus goes back to the card once the drawer (if any) has unmounted.
    useEffect(() => {
        if (!refocusId) return;
        focusCard(refocusId);
        setRefocusId(null);
    }, [refocusId, focusCard]);
    useEffect(() => {
        if (!focusPreview || !showPreview) return;
        setFocusPreview(false);
        if (!drawerMode) openBtnRef.current?.focus();
    }, [focusPreview, showPreview, drawerMode]);

    // Escape closes the inline preview (the drawer has its own Escape; open
    // menus and confirms stop their own Escape first).
    const escapeState = useRef({});
    escapeState.current = { showPreview, drawerMode, closePreview };
    useEffect(() => {
        const onKeyDown = (e) => {
            const s = escapeState.current;
            if (e.key !== 'Escape' || !s.showPreview || s.drawerMode || e.defaultPrevented) return;
            if (document.querySelector('[aria-modal="true"]')) return;
            s.closePreview();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // ── Opening the unit (U1, D7) ───────────────────────────────────────
    const viewRef = useRef({});
    viewRef.current = {
        search, typeFilter, quickView, assignee, groupBy, showClosed, previewId,
    };
    // `fromPreview`: only a unit opened from the preview brings the preview
    // back on Back; Enter / double-click come back to the plain board (T-4).
    const openUnit = useCallback((stockNumber, tab = 'activity', taskId = null, { fromPreview = false } = {}) => {
        if (!canOpenStock(stockNumber)) return;
        const stock = String(stockNumber).trim();
        saveTasksSnapshot(location, {
            ...viewRef.current,
            previewId: fromPreview ? viewRef.current.previewId : null,
            scrollY: typeof window !== 'undefined' ? window.scrollY || 0 : 0,
            boardScroll: boardRef.current?.scrollLeft || 0,
            taskId: taskId != null ? String(taskId) : null,
        });
        navigate(unitPath(stock, tab), { state: { from: fromLocation({ pathname: '/tasks' }), stocks: [stock] } });
    }, [canOpenStock, location, navigate, boardRef]);

    const previewTimer = useRef(null);
    const cancelPendingPreview = () => {
        clearTimeout(previewTimer.current);
        previewTimer.current = null;
    };
    useEffect(() => () => clearTimeout(previewTimer.current), []);

    // Enter and double-click: straight to the unit on Activity (today's path).
    const openFromCard = (stockNumber, task) => {
        cancelPendingPreview();
        openUnit(stockNumber, 'activity', task?.id);
    };
    // A plain click: the preview, when the unit is known; while the inventory
    // is still loading (or failed) there is nothing to preview, so it opens
    // the unit directly, as today (P-TASK-073).
    const activateCard = (stockNumber, task) => {
        if (unitStatusFor(task) !== 'found') {
            openFromCard(stockNumber, task);
            return;
        }
        cancelPendingPreview();
        previewTimer.current = setTimeout(() => {
            previewTimer.current = null;
            setPreviewId(String(task.id));
            setFocusPreview(true);
        }, PREVIEW_CLICK_DELAY_MS);
    };

    // Back from the unit page: scroll, board scroll and focus on the card.
    const [restoreView, setRestoreView] = useState(Boolean(restore));
    useEffect(() => {
        if (!restoreView || loading) return;
        setRestoreView(false);
        scrollInstant(restore.scrollY || 0);
        if (boardRef.current) boardRef.current.scrollLeft = restore.boardScroll || 0;
        if (restore.taskId) focusCard(restore.taskId, { preventScroll: true });
    }, [restoreView, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // QA T-5: an edge fade while more columns wait to the right.
    const [scrollRight, setScrollRight] = useState(false);
    useEffect(() => {
        const el = boardRef.current;
        if (!el) { setScrollRight(false); return undefined; }
        const check = () => setScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
        check();
        el.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(check) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            ro?.disconnect();
        };
        // Re-bound whenever the columns element may have been (re)mounted or resized.
    }, [boardRef, loading, error, groupBy, columns.length, filtered.length, showPreview, drawerMode]);

    // ── Derived for the v2 extras ───────────────────────────────────────
    const summaries = useMemo(() => quickViewSummaries(activities, now), [activities]); // eslint-disable-line react-hooks/exhaustive-deps
    const typeCounts = typeChipCounts(activities, view, now);
    const pills = filterPills({ quickView, search, typeFilter, assignee }, nameFor)
        .map((p) => ({ id: p.key, label: p.label, value: p.value }));
    const openShown = filtered.filter(isOpenTask).length;
    const closedShown = filtered.length - openShown;
    const unitCount = inventoryUnits ? inventoryUnits.length : 0;
    // P-TASK-007 / N-12: hidden while the inventory loads (0 branches).
    const wsMeta = branches ? `${count(branches, 'branch', 'branches')} · ${count(unitCount, 'unit', 'units')}` : '';
    const dealerName = dealership || '';

    const dragNote = !canEdit ? 'Read-only: actions and drag are off' : (groupBy !== 'status' ? 'Drag works in By status' : '');

    const renderBoard = () => {
        if (activities.length === 0) {
            return (
                <div className="tb2-wsempty">
                    <span className="tb2-wsempty-ic" aria-hidden="true"><DealerIcon /></span>
                    <h3>{`No tasks in ${dealerName || 'this dealership'}`}</h3>
                    <p>
                        Tasks belong to one dealership, so this board only shows
                        {' '}
                        {dealerName || 'this dealership'}
                        . Create a task from a unit&apos;s Activity tab.
                    </p>
                </div>
            );
        }
        if (filtersActive && filtered.length === 0) {
            return (
                <div className="tb2-nomatch" role="status">
                    <p>{`No tasks match these filters in ${dealerName || 'this dealership'}.`}</p>
                    <Button onClick={clearFilters}>Clear filters</Button>
                </div>
            );
        }
        if (groupBy === 'branch' && unitsLoading) {
            return <div className="tb2-nomatch" role="status"><p>Loading branches…</p></div>;
        }
        return (
            <div className={`tb2-columns${dragItem ? ' is-dragging' : ''}`} ref={boardRef} tabIndex={-1}>
                {columns.map((col) => {
                    const isDropTarget = col.droppable && Boolean(dragItem) && dropTarget === col.key;
                    const headingId = `tb2-col-${col.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                    const personEmail = col.key.startsWith('person:') && col.key !== 'person:none' ? col.key.slice(7) : null;
                    return (
                        <section
                            key={col.key}
                            className={[
                                'tb2-col',
                                dragEnabled && col.droppable ? 'can-drop' : '',
                                isDropTarget ? 'is-drop' : '',
                            ].filter(Boolean).join(' ')}
                            aria-labelledby={headingId}
                            data-col-key={col.key}
                            tabIndex={-1}
                            onDragOver={(e) => {
                                if (!dragEnabled || !col.droppable || !dragItem) return;
                                e.preventDefault();
                                if (dropTarget !== col.key) setDropTarget(col.key);
                            }}
                            onDragLeave={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget((t) => (t === col.key ? null : t));
                            }}
                            onDrop={(e) => {
                                if (!col.droppable) return;
                                e.preventDefault();
                                handleDrop(col.key);
                            }}
                        >
                            <div className="tb2-col-head">
                                {personEmail && <Avatar email={personEmail} name={col.label} size="xs" />}
                                {col.key === 'person:none' && <span className="tb2-av tb2-av--none tb2-av--xs" aria-hidden="true" />}
                                {!col.key.startsWith('person:') && (
                                    <span className={`tb2-glyph tb2-glyph--${col.tone}`}><StatusGlyph tone={col.tone === 'none' ? 'branch' : col.tone} /></span>
                                )}
                                <h3 id={headingId} title={col.label}>{col.label}</h3>
                                <span className="tb2-col-ct" aria-label={`${col.tasks.length} tasks`}>{col.tasks.length}</span>
                            </div>
                            <div className="tb2-col-cards">
                                {isDropTarget && <div className="tb2-drop-slot" aria-hidden="true">{`Move to ${col.label}`}</div>}
                                {col.tasks.length === 0 ? (
                                    <div className="tb2-col-empty">{columnEmptyText({ filtersActive, dragEnabled, droppable: col.droppable })}</div>
                                ) : col.tasks.map((task) => (
                                    <TaskCardV2
                                        key={task.id}
                                        task={task}
                                        unit={unitFor(task)}
                                        unitStatus={unitStatusFor(task)}
                                        assigneeName={task.assigned_to ? nameFor(task.assigned_to) : undefined}
                                        canEdit={canEdit}
                                        draggable={dragEnabled}
                                        selected={showPreview && String(task.id) === previewId}
                                        hintId={KBD_HINT_ID}
                                        onStatusChange={handleStatusChange}
                                        onQuickStatus={handleQuickStatus}
                                        onNavigate={handleNavigate}
                                        onOpenUnit={openFromCard}
                                        onActivate={activateCard}
                                        onDelete={handleDelete}
                                        onEdit={handleEdit}
                                        dragging={dragItem?.id === task.id}
                                        onDragStart={setDragItem}
                                        onDragEnd={endDrag}
                                    />
                                ))}
                            </div>
                        </section>
                    );
                })}
            </div>
        );
    };

    const previewProps = showPreview ? {
        task: previewTask,
        unit: previewUnit,
        assigneeName: previewTask.assigned_to ? nameFor(previewTask.assigned_to) : undefined,
        onOpen: (tab) => openUnit(previewTask.stock_number, tab, previewTask.id, { fromPreview: true }),
    } : null;

    return (
        <div className="tb2" data-testid="tasks-v2">
            <main className="tb2-page">
                <div className="tb2-head">
                    <h2>Task Board</h2>
                    <div className="tb2-ws" role="group" aria-label="Dealership">
                        <span className="tb2-ws-ic" aria-hidden="true"><DealerIcon /></span>
                        <span className="tb2-ws-txt">
                            <span className="v2-eyebrow">Dealership</span>
                            <b>{dealerName}</b>
                        </span>
                        {wsMeta && <span className="tb2-ws-meta">{wsMeta}</span>}
                        {canSwitch && (
                            <Button size="sm" onClick={switchDealership} aria-label="Switch dealership">
                                Switch <CaretIcon />
                            </Button>
                        )}
                    </div>
                    {readOnly && (
                        <span className="tb2-ro" title="You can look at this dealership's data but not change it">Read-only</span>
                    )}
                    <span className="tb2-scope">
                        {readOnly ? 'Viewing as SAG staff. Only this dealership’s tasks are shown.' : 'Only this dealership’s tasks are shown.'}
                    </span>
                    <span className="tb2-spacer" />
                    <span className="tb2-date">{formatToday(today)}</span>
                </div>

                <p id={KBD_HINT_ID} className="v2-sr">{kbdHintText(canEdit)}</p>

                {loading && <LoadingState label="Loading tasks..." />}
                {!loading && error && (
                    <ErrorState
                        title="Tasks could not be loaded"
                        message="The task service did not answer. Your tasks are safe; try again in a moment."
                        onRetry={retry}
                    />
                )}
                {!loading && !error && (
                    <>
                        <div className="tb2-tiles" role="group" aria-label="Quick views">
                            {QUICK_VIEWS.map((q) => {
                                const s = summaries[q.id];
                                return (
                                    <button
                                        key={q.id}
                                        type="button"
                                        className={`tb2-tile tb2-tile--${q.id}`}
                                        aria-pressed={quickView === q.id}
                                        onClick={() => toggleQuickView(q.id)}
                                    >
                                        <span className="tb2-tile-t">
                                            <i aria-hidden="true" />
                                            {q.label}
                                            <span className="tb2-tile-flt" aria-hidden="true">Filter</span>
                                        </span>
                                        <span className="tb2-tile-row">
                                            <span className="tb2-tile-n">{quickCounts[q.id]}</span>
                                            <span className="tb2-tile-p">{s.text}</span>
                                        </span>
                                        {q.id === 'open' && (
                                            <span className="tb2-mini" aria-hidden="true">
                                                {s.count ? (
                                                    <>
                                                        <span className="tb2-mini-open" style={{ flexGrow: s.notStarted }} />
                                                        <span className="tb2-mini-prog" style={{ flexGrow: s.inProgress }} />
                                                    </>
                                                ) : <span className="tb2-mini-none" />}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className={`tb2-work${showPreview && !drawerMode ? ' has-preview' : ''}`}>
                            <section className="v2-board tb2-board" aria-label="Task board">
                                <div className="tb2-ctl">
                                    <div className="tb2-row">
                                        <label className="v2-search tb2-search">
                                            <span className="v2-sr">Search tasks</span>
                                            <SearchIcon />
                                            <input
                                                ref={searchRef}
                                                type="search"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder="Stock #, model, task or branch"
                                                autoComplete="off"
                                            />
                                        </label>
                                        <div className="tb2-who">
                                            <UserSearch
                                                users={users}
                                                value={assignee}
                                                onChange={setAssignee}
                                                activities={activities}
                                                placeholder="All assignees"
                                                allLabel="All assignees"
                                            />
                                        </div>
                                        <span className="tb2-spacer" />
                                        <div className="v2-seg" role="group" aria-label="Group cards by">
                                            {GROUP_OPTIONS.map((g) => (
                                                <button key={g.id} type="button" aria-pressed={groupBy === g.id} onClick={() => chooseGroupBy(g.id)}>
                                                    {g.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="tb2-types" role="group" aria-label="Task type">
                                        <span className="tb2-types-l" aria-hidden="true">Type</span>
                                        {TYPE_FILTERS.map((f) => (
                                            <button
                                                key={f.id}
                                                type="button"
                                                className="tb2-chip"
                                                aria-pressed={typeFilter === f.id}
                                                onClick={() => setTypeFilter(f.id)}
                                            >
                                                {f.id !== 'all' && <i className={`tb2-dot tb2-dot--${f.id}`} aria-hidden="true" />}
                                                {f.label}
                                                <span className="tb2-chip-c">{typeCounts[f.id]}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <PillList
                                        label="Active task filters"
                                        items={pills}
                                        onRemove={clearFilter}
                                        onClearAll={clearFilters}
                                        clearLabel="Clear filters"
                                        fallbackFocusRef={searchRef}
                                    />
                                    <div className="tb2-ctl-foot">
                                        <span className="tb2-count">
                                            <b>{openShown}</b> open
                                            {showClosed && <>{' · '}<b>{closedShown}</b> closed</>}
                                        </span>
                                        <button
                                            type="button"
                                            className="tb2-sw"
                                            aria-pressed={showClosed}
                                            onClick={() => chooseShowClosed(!showClosed)}
                                        >
                                            <span className="tb2-sw-track" aria-hidden="true" />
                                            {showClosed ? `Show closed (last ${CLOSED_WINDOW_DAYS} days)` : `Show closed (${hiddenClosed})`}
                                        </button>
                                        {unitsLoading && <span className="tb2-hint" role="status">Loading unit details…</span>}
                                        <span className="tb2-spacer" />
                                        <span className="tb2-kbd" aria-hidden="true">
                                            <span><kbd>←</kbd><kbd>↑</kbd><kbd>↓</kbd><kbd>→</kbd> move</span>
                                            <span><kbd>Enter</kbd> open</span>
                                            {canEdit && <span><kbd>S</kbd> start</span>}
                                            {canEdit && <span><kbd>C</kbd> complete</span>}
                                            {dragEnabled && <span>or drag a card</span>}
                                        </span>
                                    </div>
                                </div>

                                <div className={`tb2-cols-wrap${scrollRight ? ' is-scroll-right' : ''}`}>{renderBoard()}</div>

                                <div className="tb2-foot">
                                    <span>
                                        Tasks for <b>{dealerName}</b>
                                        {` · closed tasks older than ${CLOSED_WINDOW_DAYS} days are hidden here; see the unit’s Activity tab`}
                                    </span>
                                    <span className="tb2-spacer" />
                                    {dragNote && <span className="tb2-dragnote">{dragNote}</span>}
                                </div>
                            </section>

                            {previewProps && !drawerMode && (
                                <aside className="tb2-preview" aria-label="Task preview">
                                    <TaskPreview {...previewProps} ref={openBtnRef} onClose={closePreview} />
                                </aside>
                            )}
                        </div>
                    </>
                )}
            </main>

            {previewProps && drawerMode && (
                <Drawer open onClose={closePreview} title="Task preview" width={380}>
                    <TaskPreview {...previewProps} />
                </Drawer>
            )}

            <footer className="tb2-appfoot">
                <span>&copy; 2026 Fusable. All rights reserved.</span>
            </footer>

            <DropCancelConfirm task={board.pendingCancel} onConfirm={board.confirmPendingCancel} onKeep={board.keepPendingCancel} />
            <ToastRegion
                toast={toast ? { key: toast.key, text: toast.text, onUndo: undo } : null}
                onDismiss={dismissToast}
            />
        </div>
    );
}

TaskBoardV2.propTypes = {
    orgName: PropTypes.string,
};

export default TaskBoardV2;
